import { Minus, Plus } from "lucide-react";
import { Reveal, Chapter } from "@/components/lunar/Reveal";

const CHIP_PRODUCTS = [
    { name: "Flyers", options: ["50", "100", "250", "500", "Custom"] },
    { name: "Beer mats / coasters", options: ["50", "100", "250", "500", "Custom"] },
    { name: "Stickers", options: ["10", "25", "50", "100", "Custom"] },
];

const STEP_PRODUCTS = ["Posters", "Tent cards", "Pull-up banners"];
const POSTER_SIZES = ["A4", "A3", "A1"];

const chipCls = (active) =>
    `rounded-full border px-4 py-2 text-sm font-medium transition-colors duration-200 ${
        active
            ? "border-[#2C3618] bg-[#2C3618] text-[#F9F8F4]"
            : "border-[#C2C0B8] text-[#3E413B] hover:border-[#899B6A]"
    }`;

const Stepper = ({ qty, onChange, testId }) => (
    <div className="flex items-center gap-3">
        <button
            type="button"
            data-testid={`${testId}-minus`}
            aria-label="Decrease quantity"
            onClick={() => onChange(Math.max(0, qty - 1))}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-[#C2C0B8] text-[#3E413B] transition-colors duration-200 hover:border-[#899B6A]"
        >
            <Minus className="h-4 w-4" />
        </button>
        <span data-testid={`${testId}-qty`} className="w-8 text-center text-sm font-bold text-[#1D201A]">
            {qty}
        </span>
        <button
            type="button"
            data-testid={`${testId}-plus`}
            aria-label="Increase quantity"
            onClick={() => onChange(qty + 1)}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-[#C2C0B8] text-[#3E413B] transition-colors duration-200 hover:border-[#899B6A]"
        >
            <Plus className="h-4 w-4" />
        </button>
    </div>
);

export const Materials = ({ items, setItems, qtys, setQtys, customQty, setCustomQty, posterSize, setPosterSize, onDone }) => {
    const slug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, "-");

    const setItem = (name, value) =>
        setItems((prev) => {
            const next = { ...prev };
            if (value) next[name] = value;
            else delete next[name];
            return next;
        });

    const setStepQty = (name, q) => {
        setQtys((prev) => ({ ...prev, [name]: q }));
        if (q > 0) setItem(name, name === "Posters" ? `${posterSize} × ${q}` : `${q}`);
        else setItem(name, null);
    };

    return (
        <section className="mx-auto max-w-6xl px-6 py-24" data-testid="materials-section">
            <Reveal className="max-w-2xl">
                <Chapter index="02" label="Build your own" />
                <h2 className="font-display mt-5 text-4xl font-medium leading-[1.1] tracking-tight text-[#1D201A] sm:text-5xl">
                    Build your own.
                </h2>
                <p className="mt-5 text-base leading-relaxed text-[#5C5F58]">
                    Order exactly what you need — or use this as a refill service if you’re already
                    a Lunar venue and just need more flyers, beer mats or stickers.
                </p>
            </Reveal>

            <Reveal delay={0.05}>
                <p className="mt-12 text-[11px] font-medium uppercase tracking-[0.22em] text-[#899B6A]">
                    Individual materials
                </p>
            </Reveal>

            <Reveal delay={0.1}>
                <div className="mt-4 max-w-3xl divide-y divide-[#E5E3DC] border-y border-[#E5E3DC]">
                    {CHIP_PRODUCTS.map(({ name, options }) => (
                        <div key={name} className="flex flex-col gap-4 py-6 sm:flex-row sm:items-center sm:justify-between" data-testid={`material-${slug(name)}`}>
                            <span className="font-display text-xl font-semibold text-[#1D201A]">{name}</span>
                            <div className="flex flex-wrap items-center gap-2">
                                {options.map((opt) => (
                                    <button
                                        key={opt}
                                        type="button"
                                        data-testid={`material-${slug(name)}-${opt.toLowerCase()}`}
                                        onClick={() => setItem(name, opt === "Custom" ? `Custom (${customQty[name] || ""})` : opt)}
                                        className={chipCls(
                                            opt === "Custom"
                                                ? Boolean(items[name]?.startsWith("Custom"))
                                                : items[name] === opt
                                        )}
                                    >
                                        {opt}
                                    </button>
                                ))}
                                {items[name]?.startsWith("Custom") && (
                                    <input
                                        type="number"
                                        min="1"
                                        data-testid={`material-${slug(name)}-custom-qty`}
                                        placeholder="Qty"
                                        value={customQty[name] || ""}
                                        onChange={(e) => {
                                            setCustomQty((p) => ({ ...p, [name]: e.target.value }));
                                            setItem(name, `Custom (${e.target.value})`);
                                        }}
                                        className="w-24 rounded-full border border-[#C2C0B8] bg-transparent px-4 py-2 text-sm text-[#1D201A] focus:border-[#4A5D23] focus:outline-none"
                                    />
                                )}
                            </div>
                        </div>
                    ))}

                    {STEP_PRODUCTS.map((name) => (
                        <div key={name} className="flex flex-col gap-4 py-6 sm:flex-row sm:items-center sm:justify-between" data-testid={`material-${slug(name)}`}>
                            <span className="font-display text-xl font-semibold text-[#1D201A]">{name}</span>
                            <div className="flex flex-wrap items-center gap-4">
                                {name === "Posters" && (
                                    <div className="flex gap-2">
                                        {POSTER_SIZES.map((size) => (
                                            <button
                                                key={size}
                                                type="button"
                                                data-testid={`material-posters-${size.toLowerCase()}`}
                                                onClick={() => {
                                                    setPosterSize(size);
                                                    if (qtys.Posters > 0)
                                                        setItem("Posters", `${size} × ${qtys.Posters}`);
                                                }}
                                                className={chipCls(posterSize === size)}
                                            >
                                                {size}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                <Stepper
                                    testId={`material-${slug(name)}`}
                                    qty={qtys[name] || 0}
                                    onChange={(q) => setStepQty(name, q)}
                                />
                            </div>
                        </div>
                    ))}
                </div>
                <button
                    data-testid="materials-request-cta"
                    onClick={onDone}
                    className="mt-8 inline-flex items-center rounded-full bg-[#2C3618] px-7 py-3.5 text-sm font-bold text-[#F9F8F4] transition-[background-color,transform] duration-300 hover:-translate-y-[2px] hover:bg-[#4A5D23]"
                >
                    Request these materials
                </button>
            </Reveal>
        </section>
    );
};
